// DrawdownChart.js

import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import i18n from '../../../i18n';
import './DrawdownChart.css';
import { timelineColors } from './palette'; // Ensure correct path to palette.js


// -------------------- Helper Functions -------------------- //

// Calculate cumulative sum for Rate of Return (ROR)
const cumSum = (levels) => {
  let total = 0;
  return levels.map((level) => {
    total += level * 100; // Convert to percentage
    return parseFloat(total.toFixed(2)); // 2 decimal precision
  });
};

// Calculate growth based on a starting amount (e.g., $10,000)
const growth10k = (levels, initialAmount = 10000) => {
  let total = initialAmount;
  return levels.map((level, index) => {
    if (index === 0) return total;
    total *= (1 + level); // Assuming level is in decimal form (e.g., 0.05 for 5%)
    return Math.round(total / 100) * 100; // Round to the nearest hundred
  });
};

// Append midnight timestamp to a date string
const appendTimestamp = (dateString) => {
  return `${dateString}T00:00:00`; // Appending midnight local time
};

// Parse both "YYYY-MM-DD" and "MM/DD/YYYY" as a local date
const parseLocalDate = (dateString) => {
  // Check if the date string is in "YYYY-MM-DD" format
  if (dateString.includes('-')) {
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // Check if the date string is in "MM/DD/YYYY" format
  else if (dateString.includes('/')) {
    const [month, day, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(NaN); // Return an invalid date if format is unrecognized
};

// Parse "MM/DD/YYYY" as a local date
const parseInputDate = (dateString) => {
  const [month, day, year] = dateString.split('/').map(Number);
  if (!month || !day || !year) return new Date(NaN); // Invalid Date
  return new Date(year, month - 1, day);
};

// Format dates as "MM/DD/YYYY"
const formatDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// Function to get start and end dates from data
function getStartAndEndDates(data) {
  let startDate = null;
  let endDate = null;

  for (let i = 0; i < data?.length; i++) {
    if (data[i]?.levelsDaily?.date?.length > 0) {
      startDate = parseLocalDate(data[i].levelsDaily.date[0]);
      endDate = parseLocalDate(data[i].levelsDaily.date[data[i].levelsDaily.date.length - 1]);
      break;
    }
  }

  return { startDate, endDate };
}

// Function to extract legend data (fund tickers)
function getLegendData(data) {
  return data.map((fund) => `${fund.ticker} - ${fund.name}`);
}

// Function to format data for tooltips
function dataFormatter(value, chartType) {
  if (chartType === 'ror') {
    return `${value}%`; // Display as percentage without decimals
  } else if (chartType === 'growth10k') {
    return `$${(value / 1000).toFixed(0)}k`; // Display as $10k, $20k, etc.
  }
  return value;
}

// Function to process and prepare chart data based on chartType
function chartData(data, chartType) {
  const dataField = 'dlyRoR';
  const dateField = 'date';

  const legendData = getLegendData(data);

  // Assign colors from timelineColors
  const series = data.map((fund, index) => {
    const startIndex = 0;
    const endIndex = fund?.levelsDaily?.[dataField]?.length - 1 || 0;

    // Slice the levelsDaily.dlyRoR array
    const levels = fund?.levelsDaily?.[dataField]?.slice(startIndex, endIndex + 1);

    if (!levels || levels.length === 0) {
      return null;
    }

    // Initialize the first level as 0.0 if chartType is 'ror'
    if (chartType === 'ror') {
      levels[0] = 0.0;
    }

    let processedData = [];
    if (chartType === 'growth10k') {
      processedData = growth10k(levels);
    } else if (chartType === 'ror') {
      processedData = cumSum(levels);
    }

    // Assign color from timelineColors
    const color = timelineColors[index % timelineColors.length];

    return {
      name: `${fund.ticker} - ${fund.name}`,
      data: processedData,
      type: 'line',
      smooth: true, // Makes the line smoother
      lineStyle: {
        color: color, // Apply color to the line
      },
      itemStyle: {
        color: color, // Apply color to the markers (dots)
      },
      emphasis: {
        itemStyle: {
          color: color, // Maintain color consistency on hover
        },
      },
    };
  }).filter(series => series !== null); // Remove any null series

  // Calculate Y-axis configuration based on chartType
  let yAxisConfig = {};

  if (chartType === 'growth10k') {
    // Find the maximum and minimum values across all series data
    let maxVal = 0;
    let minVal = Infinity;
    series.forEach(s => {
      const sMax = Math.max(...s.data);
      const sMin = Math.min(...s.data);
      if (sMax > maxVal) maxVal = sMax;
      if (sMin < minVal) minVal = sMin;
    });

    // Set Y-axis max and min with some padding
    const yAxisMax = Math.ceil(maxVal / 1000) * 1000; // Round up to nearest 1000
    const yAxisMin = minVal < 0 ? Math.floor(minVal / 1000) * 1000 : 0;

    yAxisConfig = {
      type: 'value',
      min: yAxisMin,
      max: yAxisMax,
      interval: Math.ceil((yAxisMax - yAxisMin) / 10),
      axisLabel: {
        fontSize: 12,
        color: '#050139',
        formatter: function (value) {
          return `$${(value / 1000).toFixed(0)}K`; // Formats as $10k, $20k, etc.
        },
      },
      axisLine: {
        lineStyle: {
          color: '#050139',
        },
      },
      axisTick: {
        lineStyle: {
          color: '#050139',
        },
      },
    };
  } else if (chartType === 'ror') {
    // Find the maximum and minimum values across all series data
    let maxVal = 0;
    let minVal = Infinity;
    series.forEach(s => {
      const sMax = Math.max(...s.data);
      const sMin = Math.min(...s.data);
      if (sMax > maxVal) maxVal = sMax;
      if (sMin < minVal) minVal = sMin;
    });

    // Set Y-axis max and min with some padding
    const yAxisMax = Math.ceil(maxVal / 10) * 10; // Round up to nearest 10
    const yAxisMin = minVal < 0 ? Math.floor(minVal / 10) * 10 : 0;

    yAxisConfig = {
      type: 'value',
      min: yAxisMin,
      max: yAxisMax,
      interval: Math.ceil((yAxisMax - yAxisMin) / 10),
      axisLabel: {
        fontSize: 12,
        color: '#050139',
        formatter: '{value}%', // Appends '%' directly
      },
      axisLine: {
        lineStyle: {
          color: '#050139',
        },
      },
      axisTick: {
        lineStyle: {
          color: '#050139',
        },
      },
      symbol: 'none', // Removes the axis symbols the ugly circle

    };
  }

  // Assuming all funds have the same dates
  const chartDates = data?.[0]?.levelsDaily?.[dateField] || [];

  const formattedDates = chartDates.map(dateStr => formatDate(parseLocalDate(dateStr)));

  return {
    title: {
      text: '',
      left: 5,
      textStyle: {
        fontStyle: 'normal',
        fontWeight: 900,
        fontSize: 14,
        color: '#8C959B',
      },
    },
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image: 'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        let res = `${params[0].name}<br/>`;
        params.forEach(param => {
          if (param.value !== null && param.value !== undefined) {
            res += `${param.marker} ${param.seriesName} : ${dataFormatter(param.value, chartType)}<br/>`;
          }
        });
        return res;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    legend: {
      icon: 'circle', // Ensures the legend icon is a circle
      itemGap: 20,
      data: legendData,
    },
    dataZoom: [{
      type: 'inside',
      start: 0,
      end: 100,
    }, {
      type: 'slider',
      start: 0,
      end: 100,
      handleSize: '100%',
      handleStyle: {
        color: '#050139',
      },
      showDetail: false,
    }],
    xAxis: {
      type: 'category',
      data: formattedDates,
      axisLabel: {
        fontSize: 12,
        color: '#050139',
        rotate: 0,
        formatter: (value) => value // Already formatted
      },
      axisLine: {
        lineStyle: {
          color: '#050139',
        },
      },
      axisTick: {
        lineStyle: {
          color: '#050139',
        },
      },
    },
    yAxis: yAxisConfig, // Use the dynamic Y-axis configuration
    series: series
  };
}

// -------------------- React Component -------------------- //

class DrawdownChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartType: { value: 'ror', label: 'Cumulative Total Return' }, // Default to 'ror'
      startDate: null,
      endDate: null,
      chartOptions: {},
      fullXAxisData: [],    // To store the complete list of dates in "MM/DD/YYYY"
      inputStartDate: '',   // For input box
      inputEndDate: '',     // For input box
      error: '',            // For validation messages
      dateWindowOptions: [],// Options for date window selection
      selectedDateWindow: null, // Currently selected date window
    };

    // Bind the handler
    this.handleDataZoom = this.handleDataZoom.bind(this);
  }

  componentDidMount() {
    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available.' });
      return;
    }

    const { startDate, endDate } = getStartAndEndDates(data);
    const chartOptions = chartData(data, this.state.chartType.value);

    // Extract the full list of dates from the original data, stripping time
    const fullXAxisData = data?.[0]?.levelsDaily?.date || [];
    const parsedFullDates = fullXAxisData.map(dateStr => parseLocalDate(dateStr));
    const formattedFullDates = parsedFullDates.map(date => formatDate(date));

    // Extract date window options from the first fund's drawDownDetails
    let drawDownDetails = data?.[0]?.drawDownDetails || [];

    // Handle the case where drawDownDetails is a single object
    if (!Array.isArray(drawDownDetails)) {
      drawDownDetails = [drawDownDetails];
    }

    const dateWindowOptions = drawDownDetails.map((window, index) => {
      if (!window || !window.startDate || !window.endDate) {
        console.warn(`DrawDownDetails window at index ${index} is missing startDate or endDate.`);
        return null; // Skip invalid window
      }

      let startDateObj = parseLocalDate(window.startDate);
      let endDateObj = parseLocalDate(appendTimestamp(window.endDate));

      // Validate and ensure startDate <= endDate
      if (startDateObj > endDateObj) {
        console.warn(`DrawDownDetails window at index ${index} has startDate > endDate. Swapping dates.`);
        [startDateObj, endDateObj] = [endDateObj, startDateObj];
      }

      // Find the closest available startDate and endDate in parsedFullDates
      const closestStartDate = this.findClosestDate(startDateObj, parsedFullDates, 'start');
      const closestEndDate = this.findClosestDate(endDateObj, parsedFullDates, 'end');

      if (!closestStartDate || !closestEndDate) {
        console.warn(`DrawDownDetails window at index ${index} has no valid date range after matching.`);
        return null; // Skip this window
      }

      return {
        value: index, // Using index as a unique value
        label: `${closestStartDate} - ${closestEndDate}`,
        startDate: closestStartDate,
        endDate: closestEndDate,
      };
    }).filter(option => option !== null); // Remove any null options

    // Debugging: Log dateWindowOptions to verify
    console.log('Date Window Options:', dateWindowOptions);

    this.setState({
      startDate,
      endDate,
      inputStartDate: formatDate(startDate),
      inputEndDate: formatDate(endDate),
      chartOptions,
      fullXAxisData: formattedFullDates,
      dateWindowOptions,
    });
  }

  // Helper function to find the closest date in parsedFullDates
  findClosestDate = (targetDate, parsedFullDates, type) => {
    if (!(targetDate instanceof Date) || isNaN(targetDate)) return null;

    if (type === 'start') {
      // Find the first date >= targetDate
      for (let date of parsedFullDates) {
        if (date >= targetDate) {
          return formatDate(date);
        }
      }
      // If no date is >= targetDate, return the last date
      const lastDate = parsedFullDates[parsedFullDates.length - 1];
      return formatDate(lastDate);
    } else if (type === 'end') {
      // Find the last date <= targetDate
      for (let i = parsedFullDates.length - 1; i >= 0; i--) {
        if (parsedFullDates[i] <= targetDate) {
          return formatDate(parsedFullDates[i]);
        }
      }
      // If no date is <= targetDate, return the first date
      const firstDate = parsedFullDates[0];
      return formatDate(firstDate);
    }

    return null;
  }

  // Handler for chart type change
  handleChartTypeChange = (chartType) => {
    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available to change chart type.' });
      return;
    }

    const { startDate, endDate } = getStartAndEndDates(data);
    const chartOptions = chartData(data, chartType.value);

    // Extract the full list of dates from the original data, stripping time
    const fullXAxisData = data?.[0]?.levelsDaily?.date || [];
    const parsedFullDates = fullXAxisData.map(dateStr => parseLocalDate(dateStr));
    const formattedFullDates = parsedFullDates.map(date => formatDate(date));

    this.setState({
      chartType,
      chartOptions,
      startDate,
      endDate,
      inputStartDate: formatDate(startDate),
      inputEndDate: formatDate(endDate),
      fullXAxisData: formattedFullDates,
      selectedDateWindow: null, // Reset selected date window
      error: ''
    });
  };

  // Handler for date window selection change
  handleDateWindowChange = (selectedOption) => {
    if (!selectedOption) {
      // Handle the clear action by resetting the chart to show all data
      this.resetDateWindow();
      return;
    }

    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available to select date window.' });
      return;
    }

    const windowIndex = selectedOption.value;
    const drawDownDetails = Array.isArray(data[0].drawDownDetails)
      ? data[0].drawDownDetails
      : [data[0].drawDownDetails];

    if (windowIndex < 0 || windowIndex >= drawDownDetails.length) {
      this.setState({ error: 'Selected date window is invalid.' });
      return;
    }

    const selectedWindow = drawDownDetails[windowIndex];
    if (!selectedWindow || !selectedWindow.startDate || !selectedWindow.endDate) {
      this.setState({ error: 'Selected date window is missing start or end date.' });
      return;
    }

    let startDateObj = parseLocalDate(selectedWindow.startDate);
    let endDateObj = parseLocalDate(appendTimestamp(selectedWindow.endDate));

    // Ensure startDate <= endDate
    if (startDateObj > endDateObj) {
      console.warn(`Selected window has startDate > endDate. Swapping dates.`);
      [startDateObj, endDateObj] = [endDateObj, startDateObj];
    }

    const parsedFullDates = this.props.data[0].levelsDaily.date.map(d => parseLocalDate(d));
    const closestStartDate = this.findClosestDate(startDateObj, parsedFullDates, 'start');
    const closestEndDate = this.findClosestDate(endDateObj, parsedFullDates, 'end');

    if (!closestStartDate || !closestEndDate) {
      this.setState({ error: 'Selected dates are out of range.' });
      return;
    }

    const startIndex = this.state.fullXAxisData.findIndex(date => date === closestStartDate);
    const endIndex = this.state.fullXAxisData.findIndex(date => date === closestEndDate);

    if (startIndex === -1 || endIndex === -1) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }

    const totalDates = this.state.fullXAxisData.length;
    const startPercent = (startIndex / (totalDates - 1)) * 100;
    const endPercent = (endIndex / (totalDates - 1)) * 100;

    // Update dataZoom
    this.echartsRef.getEchartsInstance().dispatchAction({
      type: 'dataZoom',
      start: Math.max(0, Math.min(startPercent, 100)),
      end: Math.min(100, Math.max(endPercent, 0)),
    });

    // Update startDate and endDate in state
    const newStartDate = parseLocalDate(closestStartDate);
    const newEndDate = parseLocalDate(closestEndDate);

    this.setState({
      startDate: newStartDate,
      endDate: newEndDate,
      inputStartDate: closestStartDate,
      inputEndDate: closestEndDate,
      selectedDateWindow: selectedOption,
      error: ''
    });
  }

  // Method to reset the date window to show all data
  resetDateWindow = () => {
    const { data } = this.props;
    if (!data || data.length === 0) {
      this.setState({ error: 'No data available.' });
      return;
    }

    const { startDate, endDate } = getStartAndEndDates(data);
    const chartOptions = chartData(data, this.state.chartType.value);

    // Extract the full list of dates from the original data, stripping time
    const fullXAxisData = data[0].levelsDaily.date || [];
    const parsedFullDates = fullXAxisData.map(dateStr => parseLocalDate(dateStr));
    const formattedFullDates = parsedFullDates.map(date => formatDate(date));

    this.echartsRef.getEchartsInstance().dispatchAction({
      type: 'dataZoom',
      start: 0,
      end: 100,
    });

    this.setState({
      startDate,
      endDate,
      inputStartDate: formatDate(startDate),
      inputEndDate: formatDate(endDate),
      chartOptions,
      fullXAxisData: formattedFullDates,
      selectedDateWindow: null, // Clear the selected date window
      error: ''
    });
  }

  // Handler for input changes
  handleInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      selectedDateWindow: null, // Reset selected date window when manual input changes
      error: ''
    });
  }

  // Handler for the "Go" button click
  handleGoClick = () => {
    const { inputStartDate, inputEndDate, fullXAxisData, chartType } = this.state;
    const parsedStartDate = parseInputDate(inputStartDate);
    const parsedEndDate = parseInputDate(inputEndDate);

    if (isNaN(parsedStartDate) || isNaN(parsedEndDate)) {
      this.setState({ error: 'Please enter valid dates in MM/DD/YYYY format.' });
      return;
    }

    if (parsedStartDate > parsedEndDate) {
      this.setState({ error: 'Start date must be before End date.' });
      return;
    }

    // Find the closest available startDate and endDate in levelsDaily.date
    const parsedLevelsDates = this.props.data[0].levelsDaily.date.map(d => parseLocalDate(d));
    const closestStartDateStr = this.findClosestDate(parsedStartDate, parsedLevelsDates, 'start');
    const closestEndDateStr = this.findClosestDate(parsedEndDate, parsedLevelsDates, 'end');

    if (!closestStartDateStr || !closestEndDateStr) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }

    // Find the indices in the fullXAxisData
    const startIndex = fullXAxisData.findIndex(date => date === closestStartDateStr);
    const endIndex = fullXAxisData.findIndex(date => date === closestEndDateStr);

    if (startIndex === -1 || endIndex === -1) {
      this.setState({ error: 'Dates are out of range.' });
      return;
    }

    // Calculate the percentage positions for dataZoom
    const totalDates = fullXAxisData.length;
    const startPercent = (startIndex / (totalDates - 1)) * 100;
    const endPercent = (endIndex / (totalDates - 1)) * 100;

    // Update dataZoom using dispatchAction for better performance
    this.echartsRef.getEchartsInstance().dispatchAction({
      type: 'dataZoom',
      start: Math.max(0, Math.min(startPercent, 100)),
      end: Math.min(100, Math.max(endPercent, 0)),
    });

    // Update startDate and endDate in state
    const newStartDate = parseLocalDate(closestStartDateStr);
    const newEndDate = parseLocalDate(closestEndDateStr);

    this.setState({
      startDate: newStartDate,
      endDate: newEndDate,
      inputStartDate: closestStartDateStr,
      inputEndDate: closestEndDateStr,
      error: '',
      selectedDateWindow: null, // Reset selected date window when manual input is used
    });
  }

  // Handler for dataZoom events
  handleDataZoom = () => {
    const echartsInstance = this.echartsRef.getEchartsInstance();
    const option = echartsInstance.getOption();

    // Ensure that dataZoom exists
    if (!option.dataZoom || option.dataZoom.length === 0) {
      return;
    }

    // Assuming the first dataZoom is the 'inside' type
    const dataZoom = option.dataZoom[0];
    const start = dataZoom.start;
    const end = dataZoom.end;

    const { fullXAxisData } = this.state;

    const totalDates = fullXAxisData.length;
    const startIndex = Math.floor((start / 100) * (totalDates - 1));
    const endIndex = Math.floor((end / 100) * (totalDates - 1));

    // Get the dates corresponding to the zoom range
    const newStartDateStr = fullXAxisData[startIndex];
    const newEndDateStr = fullXAxisData[endIndex];

    // Parse the dates using the updated parseLocalDate function
    const newStartDate = parseLocalDate(newStartDateStr);
    const newEndDate = parseLocalDate(newEndDateStr);

    // Log for debugging
    console.log('Zoomed Start Date:', newStartDate);
    console.log('Zoomed End Date:', newEndDate);

    // Ensure the dates are valid before updating the state
    if (!isNaN(newStartDate.getTime()) && !isNaN(newEndDate.getTime())) {
      // Update state with the new zoomed-in date range
      this.setState({
        inputStartDate: formatDate(newStartDate), // Ensure consistent format for inputs
        inputEndDate: formatDate(newEndDate),     // Ensure consistent format for inputs
        startDate: newStartDate,
        endDate: newEndDate,
        selectedDateWindow: null, // Reset selected date window when zooming manually
        error: ''
      });
    } else {
      console.error('Invalid Dates after dataZoom:', newStartDateStr, newEndDateStr);
    }
  };

  render() {
    const {
      chartType,
      chartOptions,
      inputStartDate,
      inputEndDate,
      startDate,
      endDate,
      error,
      dateWindowOptions,
      selectedDateWindow
    } = this.state;

    console.log('**', startDate, endDate);

    // Define chartType options
    const chartTypeOptions = [
      { value: 'ror', label: 'Rate of Return (ROR)' },
      { value: 'growth10k', label: 'Growth of 10K' },
    ];

    return (
      <div>
        <div className='chartHeader'>{i18n.t('Drawdowns')}</div>
        <div className='chartSubHeader' style={{ textAlign: 'center', marginBottom: '20px', fontSize: '16px', fontWeight: 'bold' }}>
          {chartType ? chartType.label : ''} - {startDate ? formatDate(startDate) : ''} to {endDate ? formatDate(endDate) : ''}
        </div>

        {/* Controls row */}
        <div className='row' style={{ marginBottom: '20px', alignItems: 'center' }}>
          {/* Left side: Chart Type Selector */}
          <div className='col-md-3' style={{ textAlign: 'center', paddingRight: '3px', borderRadius: '0px' }}>
            <Select
              options={chartTypeOptions}
              value={chartType}
              onChange={this.handleChartTypeChange}
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: '38px',
                  borderRadius: '0px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                }),
              }}
              placeholder="Select Chart Type"
            />
          </div>

          {/* Middle: Date Window Selector */}
          <div className='col-md-3' style={{ textAlign: 'center', paddingLeft: '3px', paddingRight: '3px' }}>
            <Select
              options={dateWindowOptions}
              value={selectedDateWindow}
              onChange={this.handleDateWindowChange}
              isClearable={true}
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: '38px',
                  borderRadius: '0px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                }),
              }}
              placeholder="Select Date Window"
            />
          </div>

          {/* Right side: Input boxes and Go button */}
          <div className='col-md-6' style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <div className="fund-char-perf date-picker-container" style={{ marginRight: '10px' }}>
              <input
                type="text"
                name="inputStartDate"
                value={inputStartDate}
                onChange={this.handleInputChange}
                placeholder="Start Date (MM/DD/YYYY)"
                className="form-control"
                style={{ textAlign: 'center', paddingLeft: '5px', paddingRight: '5px', height: '38px', borderRadius: '0px', width: '200px' }}
              />
            </div>
            <div className="fund-char-perf date-picker-container" style={{ marginRight: '10px' }}>
              <input
                type="text"
                name="inputEndDate"
                value={inputEndDate}
                onChange={this.handleInputChange}
                placeholder="End Date (MM/DD/YYYY)"
                className="form-control"
                style={{ textAlign: 'center', paddingLeft: '5px', paddingRight: '5px', height: '38px', borderRadius: '0px', width: '200px' }}
              />
            </div>
            <button
              className={`dlyPerfbutton dlyPerfGoButton`}
              onClick={this.handleGoClick}
              style={{ height: '38px' }}
            >
              GO
            </button>
            {/* <button className='dlyPerfGoButton' onClick={() => this.onGoFormSubmit('go')}>
              {this.props.loadingPerformance ? <FontAwesomeIcon icon={faSpinner} spin /> : 'GO'}
            </button> */}
          </div>
        </div>

        {/* Display error message if any */}
        {error && (
          <div className='row' style={{ marginBottom: '10px' }}>
            <div className='col-md-12' style={{ color: 'red', textAlign: 'center' }}>
              {error}
            </div>
          </div>
        )}

        {/* Chart */}
        <div className='chartContainer'>
          <ReactEcharts
            option={chartOptions}
            ref={(e) => (this.echartsRef = e)}
            theme='default'
            notMerge={true}
            style={{ height: 500 }}
            onEvents={{
              'dataZoom': this.handleDataZoom, // Correct event name and handler
            }}
          />
        </div>
      </div>
    );
  }
}

// Function to map Redux state to component props
function mapStateToProps(state) {
  return {
    data: state.fundData.data,
  };
}

export default connect(mapStateToProps, {})(DrawdownChart);
